/**
 * Paper Summary Service
 * Handles all Supabase operations for paper summaries and jobs
 * Based on FRONTEND_INTEGRATION_GUIDE.md
 */

import { supabase, toError } from './supabase';
import type { PaperSummary, PaperSummaryJob, SummaryStatusCheck } from '../components/papers/types';
import type { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import {
    isSummaryLimitReachedError,
    toSummaryLimitReachedError,
} from './entitlements';
import {
    isPdfAvailabilityRequiredError,
    pdfAvailabilityRequiredMessage,
} from './pdfAvailability';
import { getClientLocaleSnapshot } from './clientSessionMetadata';

// Type assertion helper for tables not in generated types
// paper_summary_jobs table exists but isn't in the client-side generated types
const db = supabase as SupabaseClient<any>;

/**
 * Check if paper already has summary or pending job
 * Returns the current status and associated data
 */
export async function getSummaryStatus(paperId: string): Promise<SummaryStatusCheck> {
    // Check for completed summary using fetchSummary which queries the view
    try {
        const summary = await fetchSummary(paperId);
        if (summary) {
            return { status: 'available', summary };
        }
    } catch (error) {
        console.error('[paperSummaryService] Error in getSummaryStatus:', error);
        throw toError(error);
    }

    // Check for in-progress or queued job
    const { data: job, error: jobError } = await db
        .from('paper_summary_jobs')
        .select('*')
        .eq('paper_id', paperId)
        .in('status', ['queued', 'processing'])
        .order('queued_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (jobError) {
        console.error('[paperSummaryService] Error fetching job:', jobError);
        throw toError(jobError);
    }

    if (job) {
        return {
            status: job.status as 'queued' | 'processing',
            job: job as PaperSummaryJob
        };
    }

    // Check for failed job
    const { data: failedJob, error: failedError } = await db
        .from('paper_summary_jobs')
        .select('*')
        .eq('paper_id', paperId)
        .eq('status', 'failed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

    if (failedError) {
        console.error('[paperSummaryService] Error fetching failed job:', failedError);
        throw toError(failedError);
    }

    if (failedJob) {
        return { status: 'failed', job: failedJob as PaperSummaryJob };
    }

    return { status: 'not_requested' };
}

/**
 * Create a new summarization job
 * User-requested jobs get priority 10 (highest)
 */
export async function createSummaryJob(
    paperId: string,
    projectId: string | null,
    userId: string,
    preferredLanguageTag?: string | null
): Promise<PaperSummaryJob> {
    console.log('[paperSummaryService] Creating summary job:', { paperId, projectId, userId });

    const { data, error } = await db
        .from('paper_summary_jobs')
        .insert({
            paper_id: paperId,
            status: 'queued',
            priority: 10,
            triggered_by: 'user_request',
            user_id: userId,
            project_id: projectId,
            max_attempts: 3,
            locale_snapshot: getClientLocaleSnapshot(preferredLanguageTag),
        })
        .select()
        .single();

    if (error) {
        // Check for duplicate job error
        if (error.code === '23505') {
            console.error('[paperSummaryService] Duplicate job error');
            throw new Error('A summary job already exists for this paper');
        }
        if (isSummaryLimitReachedError(error)) {
            throw toSummaryLimitReachedError(error);
        }
        if (isPdfAvailabilityRequiredError(error)) {
            throw new Error(pdfAvailabilityRequiredMessage());
        }
        if (error.code === '42501') {
            throw new Error(
                'This paper must be in a research digest you can summarize before a summary job can be created.'
            );
        }
        console.error('[paperSummaryService] Error creating job:', error);
        throw toError(error);
    }

    console.log('[paperSummaryService] Job created successfully:', data.id);
    return data as PaperSummaryJob;
}

/**
 * Subscribe to real-time job updates
 * Returns subscription object that must be unsubscribed on cleanup
 */
export function subscribeToJob(
    jobId: string,
    onUpdate: (job: PaperSummaryJob) => void
): RealtimeChannel {
    console.log('[paperSummaryService] Subscribing to job:', jobId);

    const subscription = supabase
        .channel(`job:${jobId}`)
        .on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'paper_summary_jobs',
                filter: `id=eq.${jobId}`,
            },
            (payload) => {
                console.log('[paperSummaryService] Job updated:', payload.new);
                onUpdate(payload.new as PaperSummaryJob);
            }
        )
        .subscribe();

    return subscription;
}

/**
 * Fetch the completed active summary for a paper
 */
export async function fetchSummary(paperId: string): Promise<PaperSummary | null> {
    console.log('[paperSummaryService] Fetching summary for paper:', paperId);

    const { data, error } = await supabase
        .from('summary_full_view')
        .select('*')
        .eq('paper_id', paperId)
        .eq('is_active', true)
        .eq('status', 'completed')
        .maybeSingle();

    if (error) {
        console.error('[paperSummaryService] Error fetching summary:', error);
        throw toError(error);
    }

    if (!data) {
        console.log('[paperSummaryService] No summary data returned');
        return null;
    }

    // Transform the view data to match PaperSummary type
    // The view returns fields as JSON and limitations as JSON array
    console.log('[paperSummaryService] Raw data from summary_full_view:', data);
    console.log('[paperSummaryService] Fields:', data.fields);
    console.log('[paperSummaryService] Limitations:', data.limitations);
    console.log('[paperSummaryService] Summary fetched successfully');

    const transformedSummary = {
        summary_id: data.summary_id!,
        paper_id: data.paper_id!,
        model_used: data.model_used,
        status: data.status,
        is_active: data.is_active,
        summarized_at: data.summarized_at,
        input_tokens: data.input_tokens,
        output_tokens: data.output_tokens,
        created_at: data.created_at,
        total_citation_segments: data.total_citation_segments,
        total_unique_chunks: data.total_unique_chunks,
        fields: (data.fields || {}) as PaperSummary['fields'],
        limitations: (data.limitations || []) as unknown as PaperSummary['limitations']
    } as PaperSummary;

    console.log('[paperSummaryService] Transformed summary:', transformedSummary);

    return transformedSummary;
}

/**
 * Cancel a queued job
 * Only works for jobs with status='queued'
 */
export async function cancelJob(jobId: string): Promise<void> {
    console.log('[paperSummaryService] Cancelling job:', jobId);

    const { error } = await db
        .from('paper_summary_jobs')
        .update({
            status: 'cancelled',
            completed_at: new Date().toISOString(),
        })
        .eq('id', jobId)
        .eq('status', 'queued');

    if (error) {
        console.error('[paperSummaryService] Error cancelling job:', error);
        throw toError(error);
    }

    console.log('[paperSummaryService] Job cancelled successfully');
}
